<template>
  <div class="login-box">
    <invitation-header v-if="hasInvitation" :invitation="invitation" />
    <h2 v-else class="text-center">{{ $t("components.login.box.login") }}</h2>

    <i18n path="components.login.box.not_registered.content" tag="p">
      <template #register_link>
        <b-link to="/register">{{
          $t("components.login.box.not_registered.register_link_content")
        }}</b-link>
      </template>
    </i18n>

    <div class="google-login">
      <google-auth-button :label="$t('components.login.box.google')" />
    </div>

    <div class="form__separator">
      <span class="form__separator__text">{{ $t("components.login.box.or") }}</span>
    </div>

    <b-form class="login-box__form" novalidate @submit.prevent="login">
      <b-form-group :label="$t('components.login.box.email')">
        <b-form-input
          v-model="email"
          name="email"
          autofocus
          type="email"
          required
          :placeholder="$t('components.login.box.email')"
        />
      </b-form-group>

      <password-input
        v-model="password"
        name="password"
        required
        :label="$t('components.login.box.password')"
        :placeholder="$t('components.login.box.password')"
      />

      <b-form-group>
        <b-form-checkbox v-model="rememberMe" inline>
          {{ $t("components.login.box.remember_me") }}
        </b-form-checkbox>
      </b-form-group>

      <b-alert v-if="hasInvitation && !invitation.accountExists" show variant="warning">
        <p>
          {{ $t("components.login.box.invitation_not_matching_account") }}
        </p>

        <icon-button variant="success" to="/register/1" replace>{{
          $t("components.login.box.register")
        }}</icon-button
        >&nbsp;
        <icon-button variant="link" :disabled="loading" @click="clearInvitationAndLogin">
          {{ $t("components.login.box.login_without_invitation") }}
        </icon-button>
      </b-alert>

      <b-form-group v-else class="mb-1">
        <icon-button type="submit" :loading="loading" variant="success" block>
          {{ $t("components.login.box.login_submit") }}
        </icon-button>
      </b-form-group>
    </b-form>

    <div class="text-right">
      <router-link to="/password/request">
        <small>{{ $t("components.login.box.forgot_password") }}</small>
      </router-link>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, ref } from "vue";
import InvitationHeader from "@/components/Invitation/InvitationHeader.vue";
import GoogleAuthButton from "@/components/Misc/GoogleAuthButton.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PasswordInput from "@/components/Forms/PasswordInput.vue";

export default defineComponent({
  name: "LoginBox",
  components: {
    IconButton,
    InvitationHeader,
    GoogleAuthButton,
    PasswordInput,
  },
  setup() {
    const instance = getCurrentInstance();
    const vm = instance.proxy;

    const password = ref("");

    const loading = computed(() => vm.$store.state.loading);

    const email = computed({
      get() {
        return vm.$store.state.login.email;
      },
      set(value) {
        vm.$store.commit("login/email", value);
      },
    });

    const rememberMe = computed({
      get() {
        return vm.$store.state.login.rememberMe;
      },
      set(value) {
        vm.$store.commit("login/rememberMe", value);
      },
    });

    const invitation = computed(() => vm.$store.state.invitation);

    const hasInvitation = computed(() => invitation.value.id && invitation.value.code);

    const login = async () => {
      try {
        await vm.$store.dispatch("login", {
          email: email.value,
          password: password.value,
        });

        // We do not fully validate the invitation here prior to logging in, like we do on
        // registration, since blocking connection (when a user knows their credentials) creates
        // unneeded friction. During registration, invitation validation allows us to avoid wrong
        // accounts from being created.
        if (hasInvitation.value) {
          vm.$router.replace({ path: "/invitation/accept" });
          return;
        }

        if (vm.$route.query.r) {
          // Catch redirect exception since redirect is fully expected.
          vm.$router.replace(vm.$route.query.r).catch(() => {});
          return;
        }

        if (vm.$store.state.user.role === "admin") {
          vm.$router.replace("/admin/dashboard");
          return;
        }

        vm.$router.replace("/app");
      } catch (e) {
        if (e.request?.status === 401) {
          vm.$store.commit("addNotification", {
            content: vm.$t("components.login.box.invalid_credentials"),
            title: vm.$t("components.login.box.login_error"),
            variant: "danger",
            type: "login",
          });
        }
      }
    };

    const clearInvitationAndLogin = () => {
      vm.$store.commit("invitation/clear");
      login();
    };

    return {
      password,
      loading,
      email,
      rememberMe,
      invitation,
      hasInvitation,
      login,
      clearInvitationAndLogin,
    };
  },
});
</script>

<style lang="scss">
.login-box {
  .login-box__form {
    margin-top: 2rem;
  }

  .google-login {
    margin-top: 2rem;
    text-align: center;
  }
}
</style>
